import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { I18N_NAMESPACES } from '../../constants/i18nConstants';
import { getIn, useFormikContext } from 'formik';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmailChangeHandler } from './EmailChangeHandler';
import { validateEmail, validateEmailExists } from './EmailValidationUtil';
import FormValidationUtil from '../../util/FormValidationUtil';
import { Text, usePrevious } from '@as/cotopaxi';
import * as Yup from 'yup';
import { EmailError } from './EmailError';
import { EmailValidationResponseStatus } from '@as-react/common-types';
export const EmailValidationWrapper = ({ emailFieldKey, checkExists, errorMessages, sendEmail, isCollapsedForm = false, children, setIsCollapsedForm, skipValidationFor, }) => {
    var _a;
    const { values, errors, setFieldValue, isSubmitting, status, setStatus } = useFormikContext();
    const emailValue = getIn(values, emailFieldKey);
    const emailError = getIn(errors, emailFieldKey);
    const emailValuePrev = usePrevious(emailValue);
    const isEqualValue = emailValue === emailValuePrev;
    const validatedEmail = useRef(null);
    const { t } = useTranslation(I18N_NAMESPACES.buy);
    const [suggestedEmail, setSuggestedEmail] = useState();
    const [emailStatus, setEmailStatus] = useState(undefined);
    const [skipEmailValidation, setSkipEmailValidation] = useState(Boolean(skipValidationFor && skipValidationFor === emailValue));
    const { defaultRequestParams, servicesEndpoint } = useAemConfig();
    const requiredMessage = (_a = errorMessages.required) !== null && _a !== void 0 ? _a : t('buy:buy.login.email.error.required');
    useEffect(() => {
        setStatus(Object.assign(Object.assign({}, status), { emailError: emailError }));
    }, [emailError]);
    const validateEmailCallback = useCallback(validateEmail(servicesEndpoint, skipEmailValidation, emailStatus, setSuggestedEmail, setEmailStatus, true, skipValidationFor), [skipEmailValidation, emailStatus, skipValidationFor]);
    const schema = Yup.string()
        .when('$regularCheck', ([regularCheck], tmpSchema) => regularCheck
        ? tmpSchema.required(requiredMessage).matches(FormValidationUtil.emailPatternRegex, errorMessages.invalidEmail)
        : tmpSchema)
        .when('$backendEmailValidationEnabled', ([backendValidation], tmpSchema) => backendValidation ? tmpSchema.test(validateEmailCallback).required(requiredMessage) : tmpSchema)
        .when('$checkExists', ([contextCheckExists], tmpSchema) => contextCheckExists
        ? tmpSchema
            .test(validateEmailExists(servicesEndpoint, defaultRequestParams, errorMessages, true, true, sendEmail, setIsCollapsedForm))
            .required(requiredMessage)
        : tmpSchema)
        .when('$checkExistsWithValidation', ([checkExistsWithValidation], tmpSchema) => checkExistsWithValidation
        ? tmpSchema
            .test(validateEmailExists(servicesEndpoint, defaultRequestParams, errorMessages, true, true, sendEmail, setIsCollapsedForm, true, skipEmailValidation, emailStatus, setSuggestedEmail, setEmailStatus))
            .required(requiredMessage)
        : tmpSchema);
    const handleEmailValidation = (shouldCheckExists) => (email) => {
        if ((status === null || status === void 0 ? void 0 : status.currentFocusField) !== emailFieldKey && email === validatedEmail.current) {
            return status === null || status === void 0 ? void 0 : status.emailError;
        }
        validatedEmail.current = email;
        const backendEmailValidationEnabledContext = !(shouldCheckExists && checkExists);
        const checkExistsWithValidationContext = shouldCheckExists && checkExists;
        return schema
            .validate(email, {
            context: {
                checkExists: false,
                backendEmailValidationEnabled: backendEmailValidationEnabledContext,
                checkExistsWithValidation: checkExistsWithValidationContext,
                regularCheck: !backendEmailValidationEnabledContext && !checkExistsWithValidationContext,
            },
        })
            .then(() => undefined)
            .catch(error => error.message);
    };
    const handleEmailChange = (email) => {
        if (skipValidationFor && email === skipValidationFor) {
            setSkipEmailValidation(true);
        }
        else {
            skipEmailValidation && setSkipEmailValidation(false);
        }
    };
    const handleAcceptClick = () => {
        setEmailStatus(`${EmailValidationResponseStatus.VALID}_${suggestedEmail}`);
        setFieldValue(emailFieldKey, suggestedEmail, true);
    };
    const handleIgnoreClick = () => setSkipEmailValidation(true);
    const renderError = (msg, textProps) => (_jsx(Text, Object.assign({}, textProps, { children: _jsx(EmailError, { emailStatus: msg, suggestedEmail: suggestedEmail, requiredErrorLabel: t('account:delivery.required'), onAcceptClick: handleAcceptClick, onIgnoreClick: handleIgnoreClick }) })));
    return (_jsxs(_Fragment, { children: [children({
                validate: isCollapsedForm && isEqualValue ? undefined : handleEmailValidation(!isSubmitting),
                renderError,
            }), _jsx(EmailChangeHandler, { emailFieldKey: emailFieldKey, emailValue: getIn(values, emailFieldKey), skipEmailValidation: skipEmailValidation, onEmailChange: handleEmailChange, setEmailStatus: setEmailStatus })] }));
};
